import { useState } from "react";
import { api, daysUntil, fmtDate, money } from "../api";
import { useLang, useT } from "../i18n";
import { SC } from "../scenes";
import { Plate, useApi, useShell } from "../ui";
import { PackageCard, type PackageRow } from "./Packages";
import { ReferCards } from "./Refer";

interface Kpis { currency: string; marginEarnedYtdMinor?: number; tripsBooked: number; pipeline: Record<string, number>; openQuotes: number; perksEarned?: number }
export interface Trip { id: string; reference: string; title: string; state: string; stage: "planning" | "booking" | "travel_ready" | "travelling"; currency: string; destination?: string; departDate?: string; nights?: number; travellers: { adults: number; childAges: number[] }; client?: { name: string; email: string }; totals: { clientTotalMinor: number; agentEarnsMinor?: number }; orderReference?: string; proposalToken?: string; updatedAt: string }

const STEPS = ["profile_done", "agreement_signed", "course_done", "first_quote_sent"] as const;
const GOTO: Record<(typeof STEPS)[number], string> = { profile_done: "profile", agreement_signed: "agreement", course_done: "academy", first_quote_sent: "builder" };
const DESTS = [["LIS", "Lisbon", "city"], ["PDL", "Azores", "islands"], ["FNC", "Madeira", "coast"], ["FCO", "Rome", "hills"], ["CDG", "Paris", "city"], ["KEF", "Reykjavik", "mountains"]] as const;

export function Home() {
  const t = useT();
  const lang = useLang();
  const { me, go, openLounge, reload, toast } = useShell();
  const kpis = useApi<Kpis>("/kpis");
  const trips = useApi<{ trips: Trip[] }>("/trips");
  const ann = useApi<{ announcements: { id: string; date: string; title: string; body: string; link?: string }[] }>("/announcements");
  const pk = useApi<{ packages: PackageRow[] }>("/packages");
  const [taskIdx, setTaskIdx] = useState<number | null>(null);
  const [dialog, setDialog] = useState<"profile" | "agreement" | null>(null);
  const expert = me.expert;
  const ob = me.onboarding;
  const current = ob?.current ? STEPS.indexOf(ob.current as (typeof STEPS)[number]) : 0;
  const ti = taskIdx ?? Math.max(0, current);
  const step = STEPS[ti]!;
  const pct = ob?.pct ?? 100;
  const all = trips.data?.trips ?? [];
  const count = (s: Trip["stage"]) => all.filter((x) => x.stage === s).length;
  const latest = all[0];
  const k = kpis.data;
  const pipeline = k ? Object.entries(k.pipeline) : [];
  const attention = all.filter((x) => x.state === "viewed" || (x.stage === "booking" && (daysUntil(x.departDate) ?? 999) < 45)).slice(0, 4);
  const featured = (pk.data?.packages ?? []).filter((p) => p.exclusive).concat((pk.data?.packages ?? []).filter((p) => !p.exclusive)).slice(0, 4);

  return (
    <>
      <section className="bh" aria-label="Build a trip">
        <Plate svg={SC.svg("coast")} /><div className="shade" />
        <div className="in">
          <span className="eyebrow">{t("home.bh.eyebrow")}</span>
          <h2>{t("home.bh.title")}</h2>
          <p>{t("home.bh.text")}</p>
          <div className="bh-dests">
            {DESTS.map(([code, city, scene]) => <button key={code} type="button" onClick={() => { sessionStorage.setItem("station.dest", code); go("builder"); }}><span className="mini" dangerouslySetInnerHTML={{ __html: SC.svg(scene) }} />{city}</button>)}
            <button type="button" className="start" onClick={() => go("builder")}>{t("home.bh.scratch")}</button>
          </div>
        </div>
      </section>

      {expert && ob ? (
        <div className="hero-row">
          <section className="onboard" aria-label="Onboarding tasks">
            <div className="ring-side">
              <b>{t("home.onboarding")}</b>
              <svg className="ring" viewBox="0 0 120 120" aria-hidden="true"><circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="10" /><circle cx="60" cy="60" r="50" fill="none" stroke="#ECE3D4" strokeWidth="10" strokeLinecap="round" strokeDasharray="314" strokeDashoffset={314 - (314 * pct) / 100} transform="rotate(-90 60 60)" /><text x="60" y="68" textAnchor="middle" fill="#F7F4EE" fontSize="24" fontWeight="600" data-testid="ring-pct">{pct}%</text></svg>
            </div>
            <div className="task">
              <div className="row"><span className="eyebrow" style={{ color: "var(--on-ink-muted)" }}>{t("home.task", { n: ti + 1, of: STEPS.length })}</span><div className="arrows"><button type="button" aria-label="Previous task" onClick={() => setTaskIdx((ti + 3) % 4)}>‹</button><button type="button" aria-label="Next task" onClick={() => setTaskIdx((ti + 1) % 4)}>›</button></div></div>
              <h2>{t(`home.step.${step}.h`)}{ob.done.includes(step) ? " ✓" : ""}</h2>
              <p>{t(`home.step.${step}.p`)}</p>
              {!ob.done.includes(step) ? <button className="btn sand" type="button" onClick={() => (GOTO[step] === "profile" || GOTO[step] === "agreement" ? setDialog(GOTO[step] as "profile" | "agreement") : go(GOTO[step]))}>{t(`home.step.${step}.b`)}</button> : null}
              <div className="dots">{STEPS.map((s, i) => <i key={s} className={i === ti ? "on" : ob.done.includes(s) ? "done" : ""} />)}</div>
            </div>
          </section>
          <section className="card status" aria-label="Certification">
            <div className="eyebrow">{t("home.status")}</div>
            <div className="tier" style={{ marginTop: ".5rem" }}><b>GETAWAYS</b><span>{me.certified ? t(`tier.${me.tier?.next?.key ?? me.partner.tier}`).toUpperCase() : t("common.certified").toUpperCase()}</span></div>
            <p className="muted">{me.certified ? t("home.status.certified") : t("home.status.todo")}</p>
            <div className="req">{STEPS.slice(0, 3).map((s) => <div key={s} className={ob.done.includes(s) ? "done" : ""}><i>{ob.done.includes(s) ? "✓" : ""}</i>{t(`home.step.${s}.h`)}</div>)}</div>
            <div className="bar"><i style={{ width: `${Math.round((STEPS.slice(0, 3).filter((s) => ob.done.includes(s)).length / 3) * 100)}%` }} /></div>
          </section>
        </div>
      ) : null}

      <section className="kpis" aria-label="Key numbers">
        {expert ? <div className="kpi"><span className="eyebrow">{t("kpi.margin")}</span><b className="mg" data-testid="kpi-margin">{money(k?.marginEarnedYtdMinor, k?.currency)}</b><small>{me.partner.payoutCurrency}</small></div> : null}
        <div className="kpi"><span className="eyebrow">{t("kpi.trips")}</span><b data-testid="kpi-trips">{k?.tripsBooked ?? "…"}</b><small>{all.filter((x) => x.state === "accepted").length} · Station</small></div>
        <div className="kpi"><span className="eyebrow">{t("kpi.pipeline")}</span><b>{pipeline.length ? pipeline.map(([c, v]) => money(v, c)).join(" + ") : money(0)}</b><small>{t("kpi.openProposals", { n: k?.openQuotes ?? 0 })}</small></div>
        {expert ? <div className="kpi"><span className="eyebrow">{t("kpi.perks")}</span><b className="mg">{k?.perksEarned ?? 0}</b><small>{t("kpi.fromLounge")}</small></div> : null}
      </section>

      <section className="card" aria-label="Needs your attention">
        <div className="card-head"><h3>{t("home.attention")}</h3><button className="link-btn" type="button" onClick={() => go("bookings")}>{t("home.allBookings")}</button></div>
        <div className="al-grid">
          {attention.length ? attention.map((x) => (
            <button key={x.id} type="button" className="al" onClick={() => go(x.stage === "planning" ? "trips" : "bookings")}>
              <span className="pill brass">{x.state === "viewed" ? "Proposal opened" : `Departs in ${daysUntil(x.departDate)} days`}</span>
              <b>{x.client?.name ?? x.title}</b><span className="muted">{x.title}</span>
            </button>
          )) : <p className="muted">{t("home.nothing")}</p>}
        </div>
      </section>

      {expert ? (
        <section className="lounge" aria-label="Departure Lounge">
          <div>
            <span className="eyebrow">{t("lounge.promo.eyebrow")}</span>
            <h3>{t("lounge.promo.title")}</h3>
            <p>{t("lounge.promo.text")}</p>
            <div className="perks-mini"><span className="pill brass">Room upgrade request</span><span className="pill brass">$50 off a package</span><span className="pill brass">Free airport transfer</span></div>
          </div>
          <button className="btn ink" type="button" onClick={() => openLounge()}>{t("lounge.play")}</button>
        </section>
      ) : null}

      <div className="two-col">
        <section className="card" aria-label="Trips">
          <div className="card-head"><h3>{t("home.trips")}</h3><button className="link-btn" type="button" onClick={() => go("trips")}>{t("home.allTrips")}</button></div>
          <div className="pipeline" data-testid="pipeline">
            {(["planning", "booking", "travel_ready", "travelling"] as const).map((s, i) => <div key={s} className={i === 0 ? "active" : ""}><span>{t(`stage.${s}`)}</span><b>{count(s)}</b></div>)}
          </div>
          {latest ? (
            <div className="trip">
              <Plate svg={SC.forCat("City Break")} />
              <div className="t">
                <b>{latest.client?.name ? `${latest.client.name} · ` : ""}{latest.title}</b>
                <div className="meta"><span>{latest.destination ?? ""}</span><span>{fmtDate(latest.departDate, lang)}{latest.nights ? ` · ${t("common.nights", { n: latest.nights })}` : ""}</span><span>{t("common.travellers", { n: latest.travellers.adults + latest.travellers.childAges.length })}</span></div>
                <div><span className="pill brass">{t(`stage.${latest.stage}`)}</span> {latest.departDate ? <span className="pill">Departs in {daysUntil(latest.departDate)} days</span> : null}</div>
              </div>
            </div>
          ) : null}
        </section>
        <section className="card" aria-label="Announcements">
          <div className="card-head"><h3>{t("home.team")}</h3><button className="link-btn" type="button" onClick={() => go("announcements")}>{t("home.seeAll")}</button></div>
          <div className="ann" data-testid="announcements">
            {(ann.data?.announcements ?? []).slice(0, 3).map((a) => <a key={a.id} href={`#${a.link ?? "announcements"}`}><span className="date">{fmtDate(a.date, lang).replace(/, \d{4}$/, "")}</span><div><b>{a.title}</b><span>{a.body}</span></div><span className="pill">{a.link ? t(`nav.${a.link}`) : "News"}</span></a>)}
          </div>
        </section>
      </div>

      <section className="lounge am" style={{ background: "var(--surface)" }} aria-label="Account manager">
        <div className="am-ph"><span className="avatar" style={{ width: 72, height: 72, fontSize: "1.4rem" }}>{me.accountManager.name.split(" ").map((w) => w[0]).join("")}</span></div>
        <div>
          <span className="eyebrow">{t("home.am.eyebrow")}</span>
          <h3>{me.accountManager.name} · {t("home.am.title")}</h3>
          <p>{t("home.am.text")} {me.accountManager.email} · {me.accountManager.phone} · {me.accountManager.hours}</p>
        </div>
        <button className="btn line" type="button" onClick={() => go("inbox")}>{t("home.am.message")}</button>
      </section>

      <ReferCards compact />

      <section className="card" aria-label="Featured packages">
        <div className="card-head"><h3>{t("home.sell")}</h3><button className="link-btn" type="button" onClick={() => go("packages")}>{t("home.allPackages")}</button></div>
        <div className="grid">{featured.map((p) => <PackageCard key={p.slug} p={p} />)}</div>
      </section>

      {dialog === "profile" ? <ProfileDialog onClose={() => setDialog(null)} onSaved={async () => { setDialog(null); await reload(); toast("Profile saved."); }} /> : null}
      {dialog === "agreement" ? <AgreementDialog onClose={() => setDialog(null)} onSigned={async () => { setDialog(null); await reload(); toast("Agreement signed."); }} /> : null}
    </>
  );
}

function ProfileDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { me } = useShell();
  const [photoUrl, setPhoto] = useState(me.profile.photoUrl ?? "");
  const [bio, setBio] = useState(me.profile.bio ?? "");
  const [err, setErr] = useState<string>();
  return (
    <div className="overlay"><div className="dialog" role="dialog" aria-modal="true" aria-label="Profile">
      <div className="dlg-top"><div className="row"><b>Your advisor profile</b><button className="x" type="button" onClick={onClose} aria-label="Close">×</button></div></div>
      <div className="m-body mform">
        <label className="f">Photo URL<input value={photoUrl} onChange={(e) => setPhoto(e.target.value)} placeholder="https://…" /></label>
        <label className="f">Short bio (20 characters or more)<textarea rows={4} value={bio} onChange={(e) => setBio(e.target.value)} /></label>
        {err ? <p className="muted">{err}</p> : null}
        <div className="q-actions"><button className="btn line" type="button" onClick={onClose}>Cancel</button><button className="btn ink" type="button" onClick={() => api("/profile", { method: "PUT", body: { ...(photoUrl ? { photoUrl } : {}), bio } }).then(onSaved, (e: Error) => setErr(e.message))}>Save profile</button></div>
      </div>
    </div></div>
  );
}

function AgreementDialog({ onClose, onSigned }: { onClose: () => void; onSigned: () => void }) {
  const { me } = useShell();
  const [signer, setSigner] = useState(me.session.name);
  const [err, setErr] = useState<string>();
  return (
    <div className="overlay"><div className="dialog" role="dialog" aria-modal="true" aria-label="Agreement">
      <div className="dlg-top"><div className="row"><b>Independent Contractor agreement</b><button className="x" type="button" onClick={onClose} aria-label="Close">×</button></div></div>
      <div className="m-body mform">
        <p className="muted">Yes Partners programme · Inovtravel USA, Inc. You sell the Getaways' trips as an independent contractor: you set your margin within your tier's cap, the client pays Inovtravel USA, Inc., and your margin is paid to you after the trip departs. You never take the client's money. Full terms: version yes-partners-ic-2026-11.</p>
        <label className="f">Type your full name to sign<input value={signer} onChange={(e) => setSigner(e.target.value)} /></label>
        {err ? <p className="muted">{err}</p> : null}
        <div className="q-actions"><button className="btn line" type="button" onClick={onClose}>Cancel</button><button className="btn ink" type="button" onClick={() => api("/agreement", { body: { signer } }).then(onSigned, (e: Error) => setErr(e.message))}>Sign</button></div>
      </div>
    </div></div>
  );
}
